import React, { Fragment, useCallback, useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Box, Flex, Icon, NakedButton, Text, Link } from 'roo-ui/components';
import { PropTypes } from 'prop-types';
import { useInView } from 'react-intersection-observer';
import useScrollDirection, { SCROLL_DIRECTIONS } from 'lib/browser/useScrollDirection';
import startCase from 'lodash/startCase';
import { useDataLayer } from 'hooks/useDataLayer';
import { mediaQuery } from 'lib/styledSystem';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { isLegacyBrowser } from 'lib/browser';
import { getBrowser } from 'store/userEnvironment/userEnvironmentSelectors';

const intersectionArea = '-20% 0px -70% 0px';
const Wrapper = styled(Box)`
  background: white;
  border-bottom: 1px solid ${themeGet('colors.greys.porcelain')};
  box-shadow: ${(props) => (props.shadow ? props.theme.shadows.result : 'none')};
  display: block;
  height: 58px;
  left: 0;
  opacity: ${(props) => (props.visible ? '1' : '0')};
  right: 0;
  transition: opacity 0.25s ease-in-out;
  top: -1px;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
  ${mediaQuery.minWidth.sm} {
    opacity: 1;
  }
`;

const JumpToLink = styled(NakedButton)`
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.1);
  min-height: 58px;
  text-align: center;
  width: 100%;

  &:hover,
  &:focus {
    text-decoration: underline;
  }

  &:focus {
    outline: 0;
    border: ${themeGet('focus.outline')};
  }

  ${mediaQuery.minWidth.sm} {
    display: none;
  }
`;

const NavLink = styled(NakedButton)`
  margin: 0 ${themeGet('space.4')};

  background: ${themeGet('colors.greys.porcelain')};
  border-bottom: 1px solid white;
  color: ${themeGet('colors.greys.charcoal')};
  font-size: ${themeGet('fontSizes.sm')};
  width: 100%;

  &:focus {
    outline: 0;
    border: ${themeGet('focus.outline')};
  }

  ${mediaQuery.minWidth.sm} {
    background: transparent;
    color: ${themeGet('colors.greys.charcoal')};
    font-size: ${themeGet('fontSizes.base')};
    border-bottom: 3px solid;
    border-color: ${(props) => (props.isActive ? themeGet('colors.brand.primary') : 'transparent')};
    transition: border-color 0.2s ease-in-out;
    width: inherit;
  }

  ${mediaQuery.minWidth.md} {
    &:hover {
      border-color: ${themeGet('colors.brand.primary')};
    }

    &:focus {
      outline: ${themeGet('focus.outline')};
    }
  }
`;

const HoverText = styled(Text)`
  transition: text-decoration 0.25s ease-in-out;

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
  }
`;

const RightIcon = styled(Icon)`
  position: absolute;
  right: 0;
  margin-right: ${themeGet('space.2')};
`;

// NOTE: menuHeight should only be used for testing since it can be
// derived automatically in a real DOM
const InPageNavBar = React.memo(({ menuItems, shadow, sticky }) => {
  const domRef = useRef(null);
  const [dropdownRef, dropdownInView] = useInView();

  const [activeSection, setActiveSection] = useState(null);
  const { emitInteractionEvent } = useDataLayer();

  const scrollDirection = useScrollDirection();
  const [menuVisible, setMenuVisible] = useState(false);
  const [wrapperVisible, setWrapperVisible] = useState(true);
  const { isLessThanBreakpoint } = useBreakpoints();

  const isMobileWidth = isLessThanBreakpoint(0);
  const showMenu = menuVisible || !isMobileWidth;

  const browser = useSelector(getBrowser);
  if (isLegacyBrowser(browser.name)) return null;

  useEffect(() => {
    const handleIntersection = (entries = []) => {
      const intersection = entries.find((entry) => entry.isIntersecting === true);
      if (!intersection) return;
      const { name } = menuItems.find((item) => item.ref.current === intersection.target);
      setActiveSection(name);
    };
    const observer = new IntersectionObserver(handleIntersection, { rootMargin: intersectionArea });
    menuItems.forEach((item) => observer.observe(item.ref.current));
    return () => observer.disconnect(); // disconnect on unmount
  }, [menuItems]);

  useEffect(() => {
    const isScrollingUp = scrollDirection === SCROLL_DIRECTIONS.UP;
    const wrapperVisible = menuVisible || dropdownInView || isScrollingUp;

    setWrapperVisible(wrapperVisible);
  }, [menuVisible, scrollDirection, dropdownInView]);

  const handleOnClick = useCallback(
    (navId) => () => {
      const menuItem = menuItems.find((item) => item.name === navId);

      if (isMobileWidth) {
        setMenuVisible(false);
      }

      setActiveSection(navId);
      emitInteractionEvent({ type: 'In Page Navigation', value: `${startCase(menuItem.text)} Selected` });
    },
    [menuItems, isMobileWidth, emitInteractionEvent],
  );

  const toggleMobileMenu = useCallback(() => {
    setMenuVisible(!menuVisible);
  }, [menuVisible]);

  return (
    <Fragment>
      <Box ref={dropdownRef} data-testid="in-page-nav-bar" />

      <Wrapper
        ref={domRef}
        position={sticky ? 'sticky' : 'relative'}
        shadow={shadow}
        visible={!isMobileWidth || wrapperVisible}
        zIndex={sticky ? 'stickyNavigation' : 'inherit'}
      >
        <JumpToLink onClick={toggleMobileMenu} aria-label="Jump to content">
          <HoverText fontSize="sm" color="greys.charcoal" mx="auto">
            Jump to
          </HoverText>
          <RightIcon name={menuVisible ? 'expandLess' : 'expandMore'} color="greys.charcoal" size={24} />
        </JumpToLink>

        {showMenu && (
          <Flex alignItems="center" flexDirection={['column', 'row']} justifyContent="center">
            {menuItems.map(({ name, text, id }) => (
              <NavLink key={name} as={Link} href={`#${id}`} onClick={handleOnClick(name)} isActive={activeSection === name} py={[3, 4]}>
                {text}
              </NavLink>
            ))}
          </Flex>
        )}
      </Wrapper>
    </Fragment>
  );
});

InPageNavBar.displayName = 'InPageNavBar';

InPageNavBar.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      ref: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({
          current: PropTypes.oneOfType([PropTypes.object, PropTypes.element]),
        }),
      ]).isRequired,
      text: PropTypes.string.isRequired,
    }),
  ),
  menuHeight: PropTypes.number,
  shadow: PropTypes.bool,
  sticky: PropTypes.bool,
};

InPageNavBar.defaultProps = {
  menuItems: [],
  menuHeight: null,
  shadow: false,
  sticky: true,
};

export default InPageNavBar;
